import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { BehaviorSubject, Observable, Subject, Subscription, timer } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { VERSION } from '../../../environments/version';
import { Capacitor } from '@capacitor/core';
import { isSameBuild } from './build-hash';

/** Shape of the Firestore appSettings/frontend document */
interface FrontendSettings {
    /** The git hash of the latest deployed build. Set after each deploy. */
    deployedHash?: string;
    /** Legacy boolean flag (still honoured if deployedHash is absent). */
    updateAvailable?: boolean;
}

/** localStorage key used to signal a post-update confirmation toast */
const FORCE_UPDATE_KEY = 'ng_force_update_pending';

/**
 * Service that detects when a new frontend version is available.
 *
 * **Detection mechanisms (both run simultaneously):**
 *
 *  1. **Firestore push** — listens to `appSettings/frontend.deployedHash`.
 *     After a deploy, set this field to the new git hash. Every connected
 *     client compares it to its own baked-in `VERSION.hash`.
 *     - Hash matches → button hidden (user already on latest)
 *     - Hash differs  → button visible (user needs to reload)
 *     This means **no manual reset is needed** — after a user reloads,
 *     their new code has the matching hash and the button auto-hides.
 *
 *     Only server-confirmed snapshots count — never the offline cache (OE-222).
 *
 *  2. **Version polling** — fetches `/assets/version.json` 3 s after load, then every 5 minutes
 *     and compares the deployed hash with the built-in hash. Fully automatic
 *     backup in case Firestore isn't reachable.
 *
 * **Post-update confirmation:**
 *  After a force-update reload, the service sets a flag in localStorage.
 *  On the next init, if that flag is present, `justUpdated` is set to true
 *  so the header can show a confirmation snackbar.
 *
 * Exposes:
 *  - `updateAvailable$` — observable boolean (true when update is available)
 *  - `justUpdated`       — true on the first load after a forced update
 *  - `forceUpdate()`     — clears caches + hard reloads
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
    private destroy$ = new Subject<void>();

    /** Firestore says a newer hash is deployed */
    private firestoreFlag = false;
    /** Version polling says a newer hash is deployed */
    private pollingFlag = false;

    private updateAvailableSubject = new BehaviorSubject<boolean>(false);
    public updateAvailable$: Observable<boolean> = this.updateAvailableSubject.asObservable();

    /** True on the first init after a successful force-update reload */
    public justUpdated = false;

    /** The currently running git hash baked into this build */
    public readonly currentHash = VERSION.hash || '';

    /** Polling interval in ms (5 minutes) */
    private static readonly POLL_INTERVAL_MS = 5 * 60 * 1000;
    /** Delay before the first version.json check */
    private static readonly FIRST_POLL_DELAY_MS = 3_000;

    private firestoreSub?: Subscription;

    constructor(
        private http: HttpClient,
        private afs: AngularFirestore
    ) {
        // The native shell's assets are BAKED into the APK — its hash can
        // never match the latest WEB deploy, so the update badge showed
        // forever and a reload could never clear it. Native updates arrive as
        // a new APK, not a reload: suppress the whole mechanism there.
        if (Capacitor.isNativePlatform()) {
            console.log('[AppUpdateService] Native shell — web update detection disabled.');
            return;
        }

        console.log('[AppUpdateService] Initialized. Current build hash:', this.currentHash);
        this.checkPostUpdateFlag();
        this.startFirestoreListener();
        this.startVersionPolling();
    }

    // ─── Post-update confirmation ──────────────────────────────────────

    /** Check if we just came back from a forced update */
    private checkPostUpdateFlag(): void {
        // Strip the cache-busting query param left by forceUpdate()
        const url = new URL(window.location.href);
        if (url.searchParams.has('_v')) {
            url.searchParams.delete('_v');
            window.history.replaceState(null, '', url.pathname + url.search + url.hash);
        }

        if (localStorage.getItem(FORCE_UPDATE_KEY)) {
            this.justUpdated = true;
            localStorage.removeItem(FORCE_UPDATE_KEY);
            console.log('[AppUpdateService] Post-update detected — new version loaded');
        }
    }

    // ─── Firestore push detection ──────────────────────────────────────

    /**
     * Listen to Firestore `appSettings/frontend` for `deployedHash`.
     * Compare against the running build hash.
     */
    private startFirestoreListener(): void {
        this.firestoreSub = this.afs
            .doc<FrontendSettings>('appSettings/frontend')
            .snapshotChanges()
            .pipe(
                // Server-confirmed snapshots only. With enablePersistence the first emission comes
                // from the offline cache, which can hold an older deployedHash — the freshly updated
                // build then showed the dot until a hard refresh (OE-222). The listener includes
                // metadata changes, so the server's confirmation arrives even when the data is unchanged.
                filter((action) => !action.payload.metadata.fromCache),
                map((action) => action.payload.data()),
                takeUntil(this.destroy$)
            )
            .subscribe({
                next: (data) => {
                    console.log('[AppUpdateService] Firestore data:', data);

                    if (data?.deployedHash) {
                        // Hash-based comparison: different hash = update available
                        this.firestoreFlag = !isSameBuild(data.deployedHash, this.currentHash);
                    } else if (data?.updateAvailable !== undefined) {
                        // Legacy boolean flag fallback
                        this.firestoreFlag = !!data.updateAvailable;
                    } else {
                        this.firestoreFlag = false;
                    }

                    this.emit();
                },
                error: (err) => {
                    // Non-critical — polling is the backup
                    console.warn('[AppUpdateService] Firestore listener error:', err);
                },
            });
    }

    // ─── Version polling detection ─────────────────────────────────────

    /**
     * Poll /assets/version.json every 5 minutes to detect a newer deployed build.
     * Uses a cache-busting query param so the service worker / CDN always returns fresh data.
     */
    private startVersionPolling(): void {
        // First check soon after load, so a just-updated page settles from a fresh source quickly.
        timer(AppUpdateService.FIRST_POLL_DELAY_MS, AppUpdateService.POLL_INTERVAL_MS)
            .pipe(
                takeUntil(this.destroy$),
                switchMap(() =>
                    this.http
                        .get<{ hash?: string; version?: string }>(`/assets/version.json?t=${Date.now()}`)
                        .pipe(catchError(() => []))
                ),
                map((response: any) => {
                    if (!response?.hash || !this.currentHash) return false;
                    return !isSameBuild(response.hash, this.currentHash);
                })
            )
            .subscribe((isNewer) => {
                this.pollingFlag = isNewer;
                this.emit();
            });
    }

    // ─── State emission ────────────────────────────────────────────────

    private emit(): void {
        const available = this.firestoreFlag || this.pollingFlag;
        console.log(
            '[AppUpdateService] emit() firestoreFlag:',
            this.firestoreFlag,
            'pollingFlag:',
            this.pollingFlag,
            '=> updateAvailable:',
            available
        );
        if (available !== this.updateAvailableSubject.value) {
            this.updateAvailableSubject.next(available);
        }
    }

    // ─── Force update action ───────────────────────────────────────────

    /**
     * Force clear all caches and hard-reload the page to pull the latest frontend code.
     *
     * JavaScript CANNOT replicate Chrome's "Empty Cache and Hard Reload" because
     * the browser's HTTP disk cache is a privileged resource. The `fetch` API cache
     * modes (`no-store`, `reload`) operate on a separate cache from the navigation
     * cache used by `location.reload()`.
     *
     * The only 100 % reliable JS cache-bust is navigating to a unique URL.
     * Firebase Hosting rewrites `/?_v=xxx` → `/index.html`, so the server
     * always returns the latest `index.html` which references new hashed bundles.
     */
    public async forceUpdate(): Promise<void> {
        try {
            // 1. Flag for post-reload confirmation
            localStorage.setItem(FORCE_UPDATE_KEY, Date.now().toString());

            // 2. Unregister all service workers
            if ('serviceWorker' in navigator) {
                const registrations = await navigator.serviceWorker.getRegistrations();
                for (const registration of registrations) {
                    await registration.unregister();
                }
                console.log(`[AppUpdateService] Unregistered ${registrations.length} service worker(s)`);
            }

            // 3. Clear all caches (Cache Storage API — used by service workers)
            if ('caches' in window) {
                const cacheNames = await caches.keys();
                for (const name of cacheNames) {
                    await caches.delete(name);
                }
                console.log(`[AppUpdateService] Cleared ${cacheNames.length} cache(s)`);
            }

            // 4. Navigate to a cache-busting URL.
            //    The unique query param forces the browser to treat this as a NEW URL,
            //    bypassing HTTP disk cache, CDN cache, and all other caching layers.
            //    Firebase rewrite ** → /index.html serves the latest build regardless.
            const url = new URL(window.location.origin + window.location.pathname);
            url.searchParams.set('_v', Date.now().toString());
            window.location.href = url.toString();
        } catch (err) {
            console.error('[AppUpdateService] Error during force update', err);
            // Last resort — plain reload
            window.location.reload();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
